import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ScrollSyncer} from 'common/util/scroll-syncer';
import {FunnelsChart, FunnelsChartAttrs} from 'common/widgets/funnels-chart';
import {FunnelsTableAttrs} from 'common/widgets/funnels-table';
import {OverviewPanelAttrs} from 'common/report/widgets/funnels/overview-panel';

import {template} from './template';
import css from './index.styl?inline';

export interface FunnelsStepsViewAttrs {
  'overview-panel-attrs': OverviewPanelAttrs;
  'chart-attrs': FunnelsChartAttrs;
  'chart-height'?: number;
  'scroll-control-offsets': {
    top: string;
    left: string;
  };
  'hide-overview'?: boolean;
  'legend-attrs'?: TODO;
  'table-loading': boolean;
  'table-attrs': FunnelsTableAttrs;
}

interface FunnelsStepsViewState {
  stepChart: FunnelsChart | null;
}

export class FunnelsStepsView extends Component<FunnelsStepsViewState, FunnelsStepsViewAttrs> {
  private scrollSyncer: ScrollSyncer;

  // Elements passed on props
  public _mouseMoveEl: HTMLElement;
  public _overallSteps: any;
  public _segmentSteps: any;
  public _comparisonSegments: any;

  static get attrsSchema(): AttrsSchema<FunnelsStepsViewAttrs> {
    return {
      ...super.attrsSchema,
      'overview-panel-attrs': `json`,
      'chart-attrs': `json`,
      'chart-height': `number`,
      'hide-overview': `boolean`,
      'legend-attrs': `json`,
      'scroll-control-offsets': `json`,
      'table-loading': `json`,
      'table-attrs': `json`,
    };
  }

  constructor() {
    super();
    this.scrollSyncer = new ScrollSyncer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.scrollSyncer.reset();
  }

  connectedCallback() {
    super.connectedCallback();

    // Trigger a re-render to ensure the scroll control gets the element
    // (the `chartScrollBoxMount` can be triggered on first render, so without
    // retriggering the element wont' get the scrollbox)
    if (this.state.stepChart) {
      this.update();
    }

    // TODO(sam) remove me when we move table reference into here
    this.dispatchEvent(
      new CustomEvent(`tableMount`, {
        detail: {el: this.el.querySelector(`.table`)},
      }),
    );
  }

  get config(): ConfigOptions<FunnelsStepsViewState> {
    return {
      template,
      css,
      useShadowDom: true,
      helpers: {
        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },
        chartScrollBoxMount: (ev) => {
          ev.stopPropagation();
          if (!this.attr(`table-attrs`)) {
            // no scroll sync without a table (dashboards)
            return;
          }
          const {vnode} = ev.detail;

          this.update({
            stepChart: vnode,
          });

          const stepChartScrollBox = vnode.getScrollBox();
          if (stepChartScrollBox) {
            this.scrollSyncer.register(stepChartScrollBox);
          }
        },
        tableScrollBoxMount: (ev) => {
          ev.stopPropagation();
          this.scrollSyncer.register(ev.detail.scrollBox);
          this.dispatchEvent(new CustomEvent(`tableScrollBoxMount`, ev));
        },
      },
    };
  }

  set overallSteps(steps: any) {
    this._overallSteps = steps;
  }
  get overallSteps() {
    return this._overallSteps;
  }

  set mousemoveEl(mousemoveEl: HTMLElement) {
    this._mouseMoveEl = mousemoveEl;

    // Trigger a re-render to ensure the scroll control gets the element
    this.update();
  }
  get mousemoveEl() {
    return this._mouseMoveEl;
  }

  set segmentSteps(segmentSteps: any) {
    this._segmentSteps = segmentSteps;
  }
  get segmentSteps() {
    return this._segmentSteps;
  }

  set comparisonSegments(comparisonSegments: any) {
    this._comparisonSegments = comparisonSegments;
  }
  get comparisonSegments() {
    return this._comparisonSegments;
  }
}

export const FunnelsStepsViewEl = defineAndMakeJsxFactory(`mp-funnels-steps-view`, FunnelsStepsView);
